import { ButtonHTMLAttributes, forwardRef, ReactElement } from 'react'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  [
    'relative inline-flex items-center rounded-full',
    'group whitespace-nowrap font-gillsans uppercase [&>*]:z-10',
    'transition-colors duration-500 ease-in-out-cubic',
    'before:absolute before:left-0 before:top-0 before:h-full before:w-full before:rounded-full',
    'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:rounded-full',
    'before:transition-colors before:duration-500 before:ease-in-out-cubic',
    'after:transition-colors after:duration-300 after:ease-in-out-cubic',
    'hover:!text-light hover:after:!bg-copper'
  ],
  {
    variants: {
      variant: {
        default: 'h-[4rem] w-auto px-[3rem] [&>span]:min-w-[10rem]',
        icon: 'p-[1.2rem]'
      },
      theme: {
        light: 'before:bg-light text-copper',
        dark: 'before:bg-dark text-light'
      }
    },
    defaultVariants: {
      variant: 'default',
      theme: 'dark'
    }
  }
)

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    startIcon?: ReactElement
  }

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant,
      theme,
      startIcon,
      className,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : 'button'

    return (
      <Component
        ref={ref}
        className={cn(buttonVariants({ variant, theme, className }))}
        {...props}
      >
        {startIcon}
        <Slottable>{children}</Slottable>
      </Component>
    )
  }
)

export default Button
